import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';

export type SearchOption = string | { value: string; label: string };

export interface SearchSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SearchOption[];
  // Supplying getLabel switches the component to object mode: `value` is the
  // underlying value and the input displays getLabel(value). Omit it for the
  // legacy free-text string mode (value === display === option string).
  getLabel?: (value: string) => string;
  placeholder?: string;
  disabled?: boolean;
  // Roving-tabindex support: place the `data-cell` marker and tabIndex on the
  // real focusable <input> so a parent grid can find/move focus to it.
  dataCell?: boolean;
  tabIndex?: number;
  // Accessible name for the combobox <input> (makes it findable by role+name).
  ariaLabel?: string;
}

interface Pos {
  top: number;
  left: number;
  width: number;
}

interface Norm {
  value: string;
  label: string;
}

export function SearchSelect({
  value,
  onChange,
  options,
  getLabel,
  placeholder,
  disabled,
  dataCell,
  tabIndex,
  ariaLabel,
}: SearchSelectProps) {
  const objectMode = !!getLabel;
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState<Pos | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const opts: Norm[] = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));

  // In object mode the search term is the internal query; in string mode it is
  // the value itself (the input edits value directly).
  const term = (objectMode ? query : value).trim().toLowerCase();
  const filtered = typed && term ? opts.filter((o) => o.label.toLowerCase().includes(term)) : opts;

  // What the text input shows.
  const display = objectMode ? (typed ? query : value ? getLabel(value) : '') : value;

  useLayoutEffect(() => {
    if (!open) return;
    function reposition() {
      const el = inputRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, width: r.width });
    }
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  function openMenu() {
    if (disabled) return;
    setTyped(false);
    setActive(0);
    setOpen(true);
  }

  function choose(opt: Norm) {
    onChange(opt.value);
    setQuery('');
    setTyped(false);
    setOpen(false);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (disabled) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) openMenu();
      else setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && filtered[active]) {
        e.preventDefault();
        choose(filtered[active]);
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        setOpen(false);
      }
    }
  }

  const caret: CSSProperties = {
    position: 'absolute',
    right: 9,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    color: 'var(--text-muted)',
    fontSize: 11,
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        className="input"
        style={{ width: '100%', paddingRight: 24 }}
        value={display}
        placeholder={placeholder}
        disabled={disabled}
        data-cell={dataCell ? '' : undefined}
        tabIndex={tabIndex}
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        autoComplete="off"
        onChange={(e) => {
          if (objectMode) setQuery(e.target.value);
          else onChange(e.target.value);
          setTyped(true);
          setActive(0);
          if (!open) setOpen(true);
        }}
        onFocus={(e) => {
          e.target.select();
          openMenu();
        }}
        onClick={openMenu}
        onKeyDown={onKeyDown}
      />
      <span style={caret}>▾</span>
      {open &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            role="listbox"
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              width: pos.width,
              maxHeight: 260,
              overflowY: 'auto',
              background: '#fff',
              border: '1px solid var(--border-strong)',
              borderRadius: 6,
              boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
              zIndex: 200,
              padding: 4,
            }}
          >
            {filtered.length === 0 ? (
              <div className="muted" style={{ padding: '8px 10px', fontSize: 13 }}>
                {objectMode ? 'ไม่พบรายการ' : 'ไม่พบรายการ — พิมพ์เพื่อเพิ่มค่าใหม่'}
              </div>
            ) : (
              filtered.map((o, i) => (
                <div
                  key={o.value}
                  role="option"
                  aria-selected={o.value === value}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(o);
                  }}
                  onMouseEnter={() => setActive(i)}
                  style={{
                    padding: '7px 10px',
                    borderRadius: 4,
                    fontSize: 14,
                    cursor: 'pointer',
                    background:
                      i === active
                        ? 'var(--brand-tint)'
                        : o.value === value
                          ? '#f3f4f6'
                          : 'transparent',
                  }}
                >
                  {o.label}
                </div>
              ))
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
